import React from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { Table, Row, Col, Input, Select, Modal, Button, Space } from 'antd';
import { LockOutlined, PlusOutlined } from '@ant-design/icons';
import * as api from 'app/shared/api';
import {
  filterData,
  getAddonProductById,
  resetAddOnModal,
  setFormErrors
} from 'app/pages/Products/actions';
import {
  FilterValueType,
  Product,
  ProductFilter,
  ProductKey
} from 'app/shared/model/ProductBase';
import { getFilteredData, getAddOnsProductsData } from '../selectors';
import { AddOnsProduct } from 'app/shared/model';
import DateHelper from 'app/helpers/dateHelper';
import filterHelper from 'app/helpers/filterHelper';
import TableSearch from 'app/shared/components/Table/TableSearch';
import TableActionButtons from 'app/shared/components/Table/TableActionButtons';
import FilterResetLink from 'app/shared/components/Table/FilterResetLink';
import TablePagination from 'app/shared/components/Table/TablePagination';
import AddEditAddonForm from './AddEditAddonForm';
import { useSignalREvent, PublishedEventType, StatusIcon } from 'app/helpers/signalRHelper';
import { sortProductsWithStatus, useProductGridSorting } from './productGridSorting';
import ProductStatusFilter from './ProductStatusFilter';
import { ProductStatusFilterValue } from './productStatusFilterUtils';
import { buildGridFilteredList } from './productGridFilterPipeline';
import { useAutoOpenProductModal } from '../hooks/useAutoOpenProductModal';

export interface AddOnsGridProps {
}

interface StateProps {
    filteredData?: Product[] | undefined,
    addOnsListData?:AddOnsProduct[]|undefined
}

interface DispatchProps {
    onCancel: () => void, 
    onFilterData: (filteredData:Product[] | undefined) => void,
    onSelectedAddonData: (addOnProduct:AddOnsProduct | undefined | void) => void
}

type InternalProps = StateProps & DispatchProps & AddOnsGridProps;

const AddOnsGridView: React.FC<InternalProps> = props => <>{AddOnsGrid(props)}</>;
import { DivisionId } from 'app/shared/division/getDivisionId';

function AddOnsGrid(props: InternalProps) {
  const [showFilters, setShowFilters] = React.useState(false);
  const [filters, setFilters] = React.useState<ProductFilter[]>([]);
  const [statusFilter, setStatusFilter] = React.useState<ProductStatusFilterValue>('all');
  const [isSearchActive, setIsSearchActive] = React.useState(false);
  const [searchFilteredData, setSearchFilteredData] = React.useState<AddOnsProduct[] | undefined>(undefined);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [itemsPerPage, setItemsPerPage] = React.useState(10);
  const { sortKey, sortOrder, getColumnSortProps, handleSorterChange } = useProductGridSorting();
  const [modalOpen, setModalOpen] = React.useState(false);
  const [modalAddOnId, setModalAddOnId] = React.useState<number | null>(null);

  const productList = props.addOnsListData ?? [];

  const publishFilteredData = React.useCallback((
    nextFilters = filters,
    nextShowFilters = showFilters,
    nextStatus = statusFilter,
    nextSearchActive = isSearchActive,
    nextSearchData = searchFilteredData,
  ) => {
    props.onFilterData(buildGridFilteredList(productList, {
      showFilters: nextShowFilters,
      filters: nextFilters,
      isSearchActive: nextSearchActive,
      searchFilteredData: nextSearchData,
      statusFilter: nextStatus,
    }));
  }, [filters, showFilters, statusFilter, isSearchActive, searchFilteredData, productList, props.onFilterData]);

  const refreshGridEvent = async () => {
    const list = await api.getAddonsProducts(DivisionId());
    publishFilteredData(filters, showFilters, statusFilter, isSearchActive, searchFilteredData);
  }

  // Subscribe to event and run refreshGridEvent when triggered
  const { status, lastEventReceived } = useSignalREvent([PublishedEventType.AddOnEventPublished], refreshGridEvent, DivisionId());

  const getAddOnProduct = (id: number) => {
    api.getAddOnById(DivisionId(), id.toString()).then(addOnProduct => {
      props.onSelectedAddonData(addOnProduct);
      setModalAddOnId(id);
      setModalOpen(true);
    });
  };

  useAutoOpenProductModal('AddOns', getAddOnProduct);

  const handleFilterChange = (
    value: any,
    field: keyof ProductKey,
    valueType: FilterValueType = FilterValueType.primaryValue
  ) => {
    const newFilters = filterHelper.getFilters(filters, value, field, valueType);
    setFilters(newFilters);
    publishFilteredData(newFilters);
    setCurrentPage(1);
  };

  const getUniqueTypeFilter = (list: AddOnsProduct[]) => {
    const texts = new Set<string>();
    list.forEach((item) => {
      const t = (item.typeText ?? '').trim();
      if (t) texts.add(t);
    });
    return Array.from(texts)
      .sort((a, b) => a.localeCompare(b))
      .map((t) => ({ label: t, value: t }));
  };

  const getUniqueUnitTypeFilter = (list: AddOnsProduct[]) => {
    const texts = new Set<string>();
    list.forEach((item) => {
      const t = (item.unitTypeText ?? '').trim();
      if (t) texts.add(t);
    });
    return Array.from(texts)
      .sort((a, b) => a.localeCompare(b))
      .map((t) => ({ label: t, value: t }));
  };

  const filteredData = (props.filteredData ? props.filteredData : productList) as AddOnsProduct[];
  const sortedData = React.useMemo(() => {
    return sortProductsWithStatus(filteredData, sortKey, sortOrder, statusFilter);
  }, [filteredData, sortKey, sortOrder, statusFilter]);
  const pagedData = TablePagination.dataPaging(sortedData, currentPage, itemsPerPage);
  const totalItems = sortedData.length;

  const handleClearFilters = () => {
    setFilters([]);
    setCurrentPage(1);
    publishFilteredData([], showFilters, statusFilter, isSearchActive, searchFilteredData);
  };

  const renderFilterRow = () => {
    if (!showFilters) return null;

    return (
      <Row gutter={12} style={{ marginBottom: 12 }}>
        <Col>
          <Input
            placeholder="Search..."
            value={filterHelper.getFieldValue(filters, 'name', '')}
            onChange={(e: { target: { value: any; }; }) => handleFilterChange(e.target.value, 'name')}
            allowClear
            style={{ width: 140 }}
          />
        </Col>
        <Col>
          <Select
            mode="multiple"
            allowClear
            placeholder="Type"
            value={filterHelper.getFieldValue(filters, 'typeText')}
            onChange={(vals) => handleFilterChange(vals, 'typeText')}
            options={getUniqueTypeFilter(productList)}
            style={{ width: 140 }}
          />
        </Col>
        <Col>
          <Select
            mode="multiple"
            allowClear
            placeholder="Unit Type"
            value={filterHelper.getFieldValue(filters, 'unitTypeText')}
            onChange={(vals) => handleFilterChange(vals, 'unitTypeText')}
            options={getUniqueUnitTypeFilter(productList)}
            style={{ width: 160 }}
          />
        </Col>
        <Col style={{ display: 'flex', alignItems: 'center' }}>
          <FilterResetLink onClick={handleClearFilters} />
        </Col>
      </Row>
    );
  };

  // Table columns
  const columns = [
    {
      title: 'Id',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      render: (text: any, record: AddOnsProduct) => (
        <>
          {text}
          {record.entityUpdate && <LockOutlined style={{ marginLeft: 4, color: '#8c8c8c' }} />}
        </>
      )
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      ...getColumnSortProps('name'),
      render: (text: string, record: AddOnsProduct) =>
        record.entityUpdate ? (
          <span style={{ color: 'rgba(0,0,0,0.65)' }}>{text} (Publishing an update)</span>
        ) : (
          <a href="#0" onClick={e => { e.preventDefault(); getAddOnProduct(Number(record.id)); }}>
            {text}
          </a>
        )
    },
    { title: 'Type', dataIndex: 'typeText', key: 'typeText', width: 140 },
    { title: 'Unit Type', dataIndex: 'unitTypeText', key: 'unitTypeText', width: 120 },
    { title: 'Min Age', dataIndex: 'minAge', key: 'minAge', width: 90,
      render: (v: number | null | undefined, r: AddOnsProduct) =>
          (v ?? r.minimumAge) == null ? '-' : String(v ?? r.minimumAge), },
    {
      title: 'Last Modified Date',
      dataIndex: 'lastModifiedDate',
      key: 'lastModifiedDate',
      width: 150,
      ...getColumnSortProps('lastModifiedDate'),
      render: (val: Date) => DateHelper.ToDateString(val)
    },
    {
      title: 'Last Updated By',
      dataIndex: 'lastUpdatedBy',
      key: 'lastUpdatedBy',
      width: 150,
      ...getColumnSortProps('lastUpdatedBy'),
    },
    { title: 'Active', dataIndex: 'active', key: 'active', width: 40,
      render: (val: boolean) => val ? <span style={{ color: 'green' }}>Yes</span> : <span style={{ color: 'red' }}>No</span>
        }
  ];

  return (
    <div>
      <Row gutter={16} style={{ marginBottom: 12 }}>
        <Col flex={2}>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>
              <TableSearch
                disabled={showFilters}
                dataList={productList}
                onSearch={(data, searchActive = false) => {
                  setCurrentPage(1);
                  setIsSearchActive(searchActive);
                  setSearchFilteredData(data as AddOnsProduct[]);
                  publishFilteredData(filters, showFilters, statusFilter, searchActive, data as AddOnsProduct[]);
                }}
              />
            </div>
            <ProductStatusFilter
              value={statusFilter}
              onChange={(nextStatus) => {
                setStatusFilter(nextStatus);
                setCurrentPage(1);
                publishFilteredData(filters, showFilters, nextStatus, isSearchActive, searchFilteredData);
              }}
            />
          </div>
        </Col>
        <Col flex={3} style={{ textAlign: 'right' }}>
          <Space>
            <StatusIcon status={status} lastEventReceived={lastEventReceived} unpublishedChanges={Array.isArray(filteredData) && filteredData.some(x => x?.entityUpdate === true)} onManualRefresh={refreshGridEvent}/>
            <TableActionButtons
              showFilters={showFilters}
              dataList={filteredData}
              onShowFiltersClick={() => {
                const nextShowFilters = !showFilters;
                setShowFilters(nextShowFilters);
                if (nextShowFilters) {
                  setFilters([]);
                  publishFilteredData([], true, statusFilter, isSearchActive, searchFilteredData);
                } else {
                  publishFilteredData(filters, false, statusFilter, isSearchActive, searchFilteredData);
                }
                setCurrentPage(1);
              }}
              canFilter
              canExport
              canEdit={false}
              canAdd={false}
              csvFileName={`${DateHelper.ToDateString(new Date(), 'yyyy.MM.dd')}_AddOns_Export`}
            />
            
            <Button
              type="primary"
              onClick={() => {
                const blank: Partial<AddOnsProduct> = {
                  id: 0,
                  name: '',
                  unitTypeId: undefined as any,
                  productCategoryId: undefined as any,
                  accountCategoryId: undefined as any,
                  numberOfOneToOneLessonsPerWeek: undefined as any,
                  typeId: undefined as any,
                  minimumAge: null,
                  excludedBookingSystemIds: [],
                  active: true
                };
                props.onSelectedAddonData(blank as AddOnsProduct);
                setModalAddOnId(0);
                setModalOpen(true);
              }}
              icon={<PlusOutlined />}
            >
              Add a new Add Ons
            </Button>
          </Space>
        </Col>
      </Row>

      {renderFilterRow()}

      <Table
        columns={columns}
        dataSource={pagedData}
        rowKey="id"
        pagination={false}
        bordered
        size="middle"
        style={{ marginBottom: 12 }}
        showSorterTooltip={false}
        onChange={(_, __, sorter: any, extra: any) => {
          handleSorterChange(sorter, extra?.action);
        }}
      />
      <TablePagination.Paging
        noOfItems={totalItems}
        itemsPerPage={itemsPerPage}
        currentPage={currentPage}
        onItemsPerPageChange={(value, page) => { setItemsPerPage(value); setCurrentPage(page); }}
        onPageChange={page => setCurrentPage(page)}
        showItemsPerPage
      />
    <Modal
      title={modalAddOnId === 0 ? 'Create Add On Product' : 'Update Add On Product'}
      open={modalOpen}
      onCancel={() => { setModalOpen(false); props.onCancel(); }}
      footer={null}
      style={{ top: 50 }}
      width={980}
      destroyOnHidden
    >
      <AddEditAddonForm
        onSuccess={() => {
          setModalOpen(false);
          props.onCancel();
        }}
      />
    </Modal> 
    </div>
  );
}

const mapStateToProps = (state: any): StateProps => ({
  filteredData: getFilteredData(state),
  addOnsListData: getAddOnsProductsData(state)
});

const mapDispatchToProps = (dispatch: Dispatch<any>): DispatchProps => ({
  onFilterData: (filteredData) => dispatch(filterData(filteredData)),
  onSelectedAddonData: addOnProduct => dispatch(getAddonProductById(addOnProduct!)),
  onCancel: () => {
    dispatch(setFormErrors([]));
    dispatch(resetAddOnModal());
    // api.getAddonsProducts(DivisionId()).then(addonProducts => {
    //   dispatch(resetAddOnModal());
    //   dispatch(listAddOnsProductsData(addonProducts));
    // });
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(AddOnsGridView);